import React, {useMemo, useState} from 'react';
import {RefreshControl, ScrollView, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchDepartments} from '@/entities/department/api';
import type {Department} from '@/entities/department/types';
import {AppButton} from '@/shared/ui/button';
import {AppInput} from '@/shared/ui/input';
import {FilterSelect} from '@/shared/ui/filter-bar';
import {useTableSort, type SortAccessors} from '@/shared/lib/table-sort';
import {IconBubble} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Icon} from '@/shared/ui/icons';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {Pager} from '@/shared/ui/pager';
import {SkeletonList} from '@/shared/ui/skeleton';
import {spacing, useTheme} from '@/shared/ui/theme';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {useClientPage} from '@/shared/lib/use-client-page';
import {departmentTypeLabelKey} from '../lib/departmentType';
import {DepartmentForm} from './DepartmentForm';

/**
 * The company's departments — the units personnel and devices are filed under. Tapping
 * a row OPENS it; the header button creates one.
 *
 * A tap used to open the three-field edit modal directly, which made the edit form the
 * whole of what a department was in this app. It is now one button inside the
 * department's own screen, next to its head-counts, its schedule, its attendance and
 * its export — so the list is a way in rather than the destination. Creating still
 * happens in the modal from here: there is no department to open yet.
 *
 * It is a scroll of ONE grouped card rather than a FlatList of outlined boxes: the
 * endpoint is capped at 100 rows (a screenful, not a feed), and a department list only
 * makes sense read as a set. Virtualisation would buy nothing and cost the grouping.
 */
const PER_PAGE = 100;
/** Строк в странице списка, и что предлагает рейка под ним. */
const PAGE_SIZE = 20;
const PAGE_SIZES = [20, 50, 100] as const;

const styles = StyleSheet.create({
  scroll: {flexGrow: 1, paddingBottom: spacing(8)},
  /* Полоса над списком — как у всех таблиц кабинета: слева то, чем сужают, справа
     то, что делают. */
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(2),
    paddingBottom: spacing(4),
  },
  toolbarSearch: {flexGrow: 1, flexBasis: 240, minWidth: 200},
  toolbarActions: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), marginLeft: 'auto'},
  pager: {paddingTop: spacing(4)},
});

/**
 * One department. The type is a subtitle, not a pill: it is a category rather than a
 * state, and a grey pill repeated down every row reads as status that never changes.
 * The code — the thing an admin cross-references against a spreadsheet — stays on the
 * right, where the eye can run down the column.
 */
function DepartmentRow({
  department,
  onPress,
}: {
  department: Department;
  onPress: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  return (
    <ListRow
      title={department.name}
      subtitle={t(departmentTypeLabelKey(department.department_type))}
      value={department.code || undefined}
      onPress={onPress}
      leading={
        <IconBubble tone={p.muted} size={38} square>
          <Icon name="building" size={19} color={p.mutedForeground} />
        </IconBubble>
      }
    />
  );
}

export function DepartmentList({
  companyId,
  onOpen,
}: {
  companyId: string;
  onOpen: (department: Department) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['departments', companyId],
    queryFn: () => fetchDepartments(companyId, {perPage: PER_PAGE}),
  });

  const items = useMemo(() => query.data?.data ?? [], [query.data]);

  /*
    Отбор и порядок — по всем отделам: список приезжает одной страницей в сто штук, и
    листает его клиент. Поиск и вид появляются только тогда, когда отделов больше
    горстки: строка поиска над списком из трёх — это лишняя строка.
  */
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const needle = search.trim().toLowerCase();
  const matched = useMemo(
    () =>
      items.filter(d => {
        if (kind && d.department_type !== kind) {
          return false;
        }
        return (
          !needle ||
          [d.name, d.code].filter(Boolean).some(f => f.toLowerCase().includes(needle))
        );
      }),
    [items, kind, needle],
  );

  const ACCESSORS: SortAccessors<Department> = {
    name: d => d.name,
    code: d => d.code,
  };
  const sorted = useTableSort(matched, ACCESSORS, {key: 'name', direction: 'asc'});

  // Отделы приезжают одной страницей в сто штук, поэтому листает клиент — стрелка под
  // списком в сеть не ходит, и сказать об этом честнее, чем сделать вид, что ходит.
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const paged = useClientPage(sorted.rows, pageSize);

  /** Виды, которые ВСТРЕЧАЮТСЯ: справочник тут был бы длиннее правды. */
  const kindOptions = [
    {value: '', label: t('actions.any')},
    ...Array.from(new Set(items.map(d => d.department_type).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({value, label: t(departmentTypeLabelKey(value))})),
  ];
  /** Поиск и фильтр появляются, только когда без них уже неудобно. */
  const searchable = items.length > 6;

  let content: React.ReactNode;

  if (query.isError) {
    content = (
      <EmptyState
        tone="error"
        title={t('department.error')}
        actionLabel={t('actions.retry')}
        onAction={() => {
          void query.refetch();
        }}
      />
    );
  } else if (query.isPending) {
    content = <SkeletonList count={5} avatar={false} />;
  } else if (items.length === 0) {
    content = (
      <EmptyState
        icon="building"
        title={t('department.empty')}
        actionLabel={t('department.add')}
        onAction={() => setCreating(true)}
      />
    );
  } else {
    content = (
      <RowGroup testID="department-list">
        {paged.rows.map(d => (
          <DepartmentRow key={d.id} department={d} onPress={() => onOpen(d)} />
        ))}
      </RowGroup>
    );
  }

  return (
    <>
      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator
        refreshControl={
          <RefreshControl
            refreshing={query.isRefetching}
            onRefresh={() => {
              void query.refetch();
            }}
            tintColor={p.primary}
            colors={[p.primary]}
          />
        }>
        {items.length > 0 || query.isPending ? (
          <View style={styles.header}>
            {searchable ? (
              <AppInput
                value={search}
                onChangeText={setSearch}
                placeholder={t('department.search')}
                icon="search"
                size={isDesktop ? 'md' : 'lg'}
                autoCapitalize="none"
                autoCorrect={false}
                style={styles.toolbarSearch}
                testID="department-search"
              />
            ) : null}
            {searchable && kindOptions.length > 2 ? (
              <FilterSelect
                label={t('department.kind')}
                value={kind}
                options={kindOptions}
                onChange={setKind}
                phone={!isDesktop}
                testID="department-kind"
              />
            ) : null}
            <View style={styles.toolbarActions}>
              <AppButton
                title={t('department.add')}
                icon="plus"
                size={isDesktop ? 'md' : 'lg'}
                onPress={() => setCreating(true)}
                testID="department-add"
              />
            </View>
          </View>
        ) : null}
        {content}
        {items.length > 0 ? (
          <Pager
            page={paged.page}
            totalPages={paged.totalPages}
            total={paged.total}
            perPage={paged.perPage}
            onPage={paged.setPage}
            perPageOptions={PAGE_SIZES}
            onPerPage={setPageSize}
            style={styles.pager}
            testID="department-pager"
          />
        ) : null}
      </ScrollView>

      {/* key flips on open so useState re-seeds from props — a reused instance would
          otherwise keep the previous form's values. */}
      <DepartmentForm
        key={`create-${creating}`}
        companyId={companyId}
        visible={creating}
        onClose={() => setCreating(false)}
      />
    </>
  );
}
